import convertInlineCssForHtml from './convertInlineCssForHtml';
import createSignatureBlock from './createSignatureBlock';
import createSubjectPickerViewState from './createSubjectPickerViewState';
import createSubjectViewState from './createSubjectViewState';
import getFromViewState from './getFromViewState';
import getSignature from './getSignature';
import { infoBarAddHandler, infoBarRemoveHandler } from './infoBarHandlers';
import isComposeDarkModeEnabled from './isComposeDarkModeEnabled';
import shouldAlwaysShowBcc from './shouldAlwaysShowBcc';
import shouldAutoAddSignature from './shouldAutoAddSignature';
import createComposePlugins from '../plugins/createComposePlugins';
// tslint:disable-next-line forbid-import
import { observable, ObservableMap } from 'mobx';
import { AppendOnSend } from 'owa-addins-core';
import { lazyGetAttachmentWellForCompose } from 'owa-attachment-well-data';
import getAttachmentWellInitialValue from 'owa-attachment-well-data/lib/utils/getAttachmentWellInitialValue';
import { createComposeFeedbackViewState } from 'owa-compose-feedback';
import { getUserMailboxInfo } from 'owa-client-ids';
import createFindPeopleFeedbackManager from 'owa-controls-findpeople-feedback-manager/lib/actions/createFindPeopleFeedbackManager';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import { lazyCreateUndo } from 'owa-editor';
import createAccCheckerViewState from 'owa-editor-acc-checker-plugin/lib/utils/createAccCheckerViewState';
import { createDockingViewState } from 'owa-editor-docking-plugin';
import createEmojiPickerViewState from 'owa-editor-emoji-picker-plugin/lib/utils/createEmojiPickerViewState';
import createAtMentionsViewState from 'owa-editor-mentions-picker-plugin/lib/utils/createAtMentionsViewState';
import { createPasteOptionViewState } from 'owa-editor-paste-option-plugin';
import createRibbonViewState from 'owa-editor-ribbonplugin/lib/utils/createRibbonViewState';
import { createTableEditViewState } from 'owa-editor-table-edit-plugin';
import { lazyAddNotification } from 'owa-header-toast-notification';
import createCLPViewState from 'owa-mail-clp/lib/utils/createCLPViewState';
import { createCompositeEditorViewState } from 'owa-editor/lib/utils/createCompositeEditorViewState';
import createPolicyTipsViewState from 'owa-policy-tips/lib/utils/createPolicyTipsViewState';
import createRecipientWell from 'owa-readwrite-recipient-well/lib/utils/createRecipientWell';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import BodyType from 'owa-service/lib/contract/BodyType';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import ImportanceType from 'owa-service/lib/contract/ImportanceType';
import ItemId from 'owa-service/lib/contract/ItemId';
import MeetingRequestMessageType from 'owa-service/lib/contract/MeetingRequestMessageType';
import Permission from 'owa-service/lib/contract/Permission';
import RightsManagementLicenseDataType from 'owa-service/lib/contract/RightsManagementLicenseDataType';
import SensitivityType from 'owa-service/lib/contract/SensitivityType';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import SmimeViewState from 'owa-smime/lib/store/schema/SmimeViewState';
import { generateSmimeViewState } from 'owa-smime/lib/utils/createSmimeDefaultViewState';
import {
    ComposeViewState,
    ComposeOperation,
    AsyncSendState,
    TextDirection,
} from 'owa-mail-compose-store';
import createClassificationViewState from 'owa-mail-message-classification/lib/utils/createClassificationViewState';
import MessageClassificationType from 'owa-service/lib/contract/MessageClassificationType';
import { isFeatureEnabled } from 'owa-feature-flags';

let nextComposeId = 0;

export function getNextComposeId(): string {
    return (nextComposeId++).toString();
}

export interface ComposeViewStateInitPropsInSync {
    appendOnSend?: AppendOnSend[];
    operation: ComposeOperation;
    bodyType: BodyType;
    itemId?: ItemId;
    subject?: string;
    newContent?: string;
    quotedBody?: string;
    to?: EmailAddressWrapper[];
    cc?: EmailAddressWrapper[];
    bcc?: EmailAddressWrapper[];
    referenceItemId?: ItemId;
    referenceItemDocumentId?: number;
    conversationId?: string;
    importance?: ImportanceType;
    alternateSignature?: string;
    preferAsyncSend?: boolean;
    from?: EmailAddressWrapper;
    pendingSocialActivityTags?: string[];
    IRMData?: RightsManagementLicenseDataType;
    useSmartResponse?: boolean;
    logTraceId?: string;
    explicitTextDirection?: TextDirection;
    isReadReceiptRequested?: boolean;
    isDeliveryReceiptRequested?: boolean;
    meetingRequestItem?: Partial<MeetingRequestMessageType>;
    sensitivity?: SensitivityType;
    internetHeaders?: ObservableMap<string, string>;
    keysOfInternetHeadersToBeRemoved?: string[];
    isInlineCompose?: boolean;
    smimeViewState?: SmimeViewState;
    existingLabelString?: string;
    isInSharedFolder?: boolean;
    folderOwnerEmailAddress?: string;
    folderPermission?: Permission;
    classification?: MessageClassificationType;
}

export interface ComposeViewStateInitProps extends ComposeViewStateInitPropsInSync {
    attachments?: AttachmentType[];
}

export function createComposeViewStateInSync(
    props: ComposeViewStateInitPropsInSync
): ComposeViewState {
    const {
        operation,
        bodyType,
        itemId,
        subject,
        newContent,
        quotedBody,
        to,
        cc,
        bcc,
        referenceItemId,
        referenceItemDocumentId,
        conversationId,
        importance,
        alternateSignature,
        preferAsyncSend,
        from,
        pendingSocialActivityTags,
        IRMData,
        useSmartResponse,
        logTraceId,
        explicitTextDirection,
        isReadReceiptRequested,
        isDeliveryReceiptRequested,
        meetingRequestItem,
        sensitivity,
        smimeViewState,
        existingLabelString,
        classification,
    } = props;
    const isInlineCompose =
        props.isInlineCompose === undefined ? !!conversationId : props.isInlineCompose;
    // For consumer mailbox, we will not show CC for new and reply if there is no CC recipient; unless AlwaysShowBcc is true.
    const isCcShown =
        (cc && cc.length > 0) || (isConsumer() ? shouldAlwaysShowBcc() : !isInlineCompose);
    const isBccShown = (bcc && bcc.length > 0) || shouldAlwaysShowBcc();
    const isRibbonShown = bodyType == 'HTML';
    let updatedContent = quotedBody || '';
    const appendOnSend: AppendOnSend[] = !!props.appendOnSend ? props.appendOnSend : [];

    // Append auto or alternate signature
    const autoSignature = shouldAutoAddSignature(operation)
        ? getSignature(bodyType)
        : alternateSignature;
    if (autoSignature) {
        // The signature goes above the existing content, i.e. for reply/forward, we want the signature to be added
        // above the quoted body
        updatedContent =
            createSignatureBlock(bodyType, autoSignature, true /*isAutoSignature*/) +
            convertInlineCssForHtml(updatedContent, bodyType);
    }

    // If we have newContent, append it to the top of the updatedContent so it's above the signature.
    updatedContent =
        (convertInlineCssForHtml(newContent, bodyType) || '') +
        convertInlineCssForHtml(updatedContent, bodyType);

    const findPeopleFeedbackManager = createFindPeopleFeedbackManager();
    const toWell = createRecipientWell(findPeopleFeedbackManager, to);
    const composeId = getNextComposeId();
    const fromViewState = getFromViewState(from, meetingRequestItem, referenceItemId);

    // We should get rid of this observable.object call once #38650 is addressed
    const composeViewState: ComposeViewState = observable.object({
        // CompositeEditorViewState
        ...createCompositeEditorViewState(updatedContent, bodyType),

        // Message properties
        operation: operation,

        // Fix 46674: Pasted inline image is lost in popout compose
        // Need to make sure viewstate property values are not undefined otherwise it will be dropped during serialization
        itemId: itemId || null,
        subject: subject || '',
        attachmentWell: getAttachmentWellInitialValue(
            false /*isReadOnly*/,
            true /*isInitialized*/,
            composeId
        ),
        referenceItemId: referenceItemId,
        referenceItemDocumentId: referenceItemDocumentId,
        importance: importance || 'Normal',
        fromViewState: fromViewState,
        subjectViewState: createSubjectViewState(composeId, operation, subject, newContent),
        subjectPickerViewState: createSubjectPickerViewState({
            supportsSubjectPicker: operation === ComposeOperation.New && !subject && !newContent,
        }),
        IRMData: IRMData,
        policyTipsViewState: createPolicyTipsViewState(),
        meetingRequestItem: meetingRequestItem,
        internetHeaders: new ObservableMap<string, string>(),
        keysOfInternetHeadersToBeRemoved: [],
        appendOnSend: appendOnSend,

        // Recipient well
        findPeopleFeedbackManager: findPeopleFeedbackManager,
        toRecipientWell: toWell,
        ccRecipientWell: isCcShown ? createRecipientWell(findPeopleFeedbackManager, cc) : null,
        bccRecipientWell: isBccShown ? createRecipientWell(findPeopleFeedbackManager, bcc) : null,

        // UI properties
        isInlineCompose: isInlineCompose,
        conversationId: conversationId || null,
        lastSaveTimeStamp: null,
        isRibbonShown: isRibbonShown,
        isSending: false,
        isSavingSpinnerShown: false,
        infoBarIds: [],
        showCompactRecipientWell: isInlineCompose && !fromViewState.isFromShown,
        dropViewState: createDropViewState(),
        isFocusWorkaroundDivShown: true,
        showSendDiscardAsIcon: false,
        lastFocusedControl: null,
        sendDisabled: false,
        smimeViewState: smimeViewState || generateSmimeViewState(),
        composeFeedbackViewState: createComposeFeedbackViewState(),

        // UpConvert / Upgrade
        quotedBody: null,
        useSmartResponse: isInlineCompose || useSmartResponse,
        numberOfWaitingAttachments: 0,

        // Plugins
        emojiPicker: createEmojiPickerViewState(),
        docking: createDockingViewState(isRibbonShown, isInlineCompose),
        ribbon: createRibbonViewState(),
        atMentions: createAtMentionsViewState(pendingSocialActivityTags),
        pasteOption: createPasteOptionViewState(),
        tableEdit: createTableEditViewState(),
        accChecker: createAccCheckerViewState(),

        isReadReceiptRequested:
            typeof isReadReceiptRequested === 'boolean' ? isReadReceiptRequested : false,
        isDeliveryReceiptRequested:
            typeof isDeliveryReceiptRequested === 'boolean' ? isDeliveryReceiptRequested : false,

        // Async send
        preferAsyncSend,
        asyncSendState: AsyncSendState.None,
        handledByDelaySend: false,
        changeAsyncSendStateTimer: 0,

        // Internal data, for multiple compose instance
        composeId,
        logTraceId,

        attachmentFilesToUpload: null,

        // Text direction that is explicitly set by the user on the root of the document.
        // Overrides UserPreference based text direction.
        explicitTextDirection: explicitTextDirection || null,

        // Message sensitivity
        sensitivity,

        lastSavedMessageBody: null,

        // If the editor is in dark mode.
        isDarkMode: isComposeDarkModeEnabled(),

        // Initialize selectedCLP Label if the labels are retrieved.
        clpViewState: createCLPViewState(existingLabelString, referenceItemId),

        // Flag that tracks if the draft was manually saved at any point.
        isManuallySaved: false,

        // Initialize message classification
        classificationViewState: isFeatureEnabled('cmp-message-classification')
            ? createClassificationViewState(classification)
            : null,
    });

    if (preferAsyncSend) {
        lazyAddNotification.import();
    }

    return composeViewState;
}

export default async function createComposeViewState(
    props: ComposeViewStateInitProps
): Promise<ComposeViewState> {
    const composeViewState = createComposeViewStateInSync(props);
    composeViewState.attachmentWell = (await lazyGetAttachmentWellForCompose.import())(
        composeViewState.composeId,
        {
            attachments: props.attachments,
            mailboxInfo: getUserMailboxInfo(),
            parentItemId: props.itemId,
            infoBarAddHandler: infoBarAddHandler(composeViewState),
            infoBarRemoveHandler: infoBarRemoveHandler(composeViewState),
        }
    );
    const undo = (await lazyCreateUndo.import())(true /*preserveSnapshots*/);
    createComposePlugins(composeViewState, undo);
    return composeViewState;
}
